import { ChevronDown, Coins, Crown, Gift } from "lucide-react";
import { useState } from "react";
import { useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { SettingCard } from "@/components/settings/setting-card";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Form } from "@/components/ui/form";
import { Skeleton } from "@/components/ui/skeleton";
import { useIsMobile } from "@/hooks/hooks/use-mobile";
import { ErrorAlert } from "@/lib/components/error-alert";
import { BackButton, BaseNavigationBar } from "@/lib/components/navigation";
import { FormActionButtons, NavigationGuardProvider } from "@/lib/components/navigation-guard";
import { PageContent } from "@/lib/components/page-content";
import { SettingsSearchTrigger } from "../../search/components/settings-search-trigger";
import {
  AdvancedSettings,
  LoyaltyProgramToggle,
  PointsEarningSettings,
  TierPreview,
} from "./components";
import { useRewardsConfig } from "./hooks/use-rewards-config";

export function RewardsSettingsPage() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { t } = useTranslation("settings");
  const { form, isLoading, saving, error, save, discard, saveField, savingFields } =
    useRewardsConfig();
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const _values = useWatch({ control: form.control });

  return (
    <NavigationGuardProvider
      hasUnsavedChanges={form.formState.isDirty}
      onSave={save}
      onDiscard={discard}
      saving={saving}
      description={t("messages.unsavedChanges")}
    >
      <BaseNavigationBar
        title={t("sections.rewards.title")}
        subtitle={t("sections.rewards.description")}
        leadElement={
          isMobile ? (
            <BackButton key="back-button" onBack={() => navigate("/settings")} />
          ) : undefined
        }
        trailingElements={[
          <SettingsSearchTrigger key="search-trigger" />,
          <FormActionButtons
            key="form-actions"
            isDirty={form.formState.isDirty}
            onSave={save}
            onDiscard={discard}
            saving={saving}
          />,
        ]}
      />

      <PageContent>
        <Form {...form}>
          <div className="space-y-6">
            <ErrorAlert error={error} />

            {isLoading ? (
              <div className="space-y-4">
                <Skeleton className="h-32 w-full" />
                <Skeleton className="h-48 w-full" />
                <Skeleton className="h-32 w-full" />
              </div>
            ) : !error ? (
              <div className="space-y-6">
                {/* Loyalty Program Master Toggle */}
                <SettingCard
                  title={t("rewards.loyaltyProgram")}
                  icon={<Gift className="h-5 w-5" />}
                >
                  <LoyaltyProgramToggle
                    saveField={saveField}
                    saving={savingFields.has("loyaltyProgramEnabled")}
                  />
                </SettingCard>

                {/* Points Earning */}
                <SettingCard title={t("rewards.earnPoints")} icon={<Coins className="h-5 w-5" />}>
                  <PointsEarningSettings
                    saveField={saveField}
                    saving={savingFields.has("enabled")}
                  />
                </SettingCard>

                {/* Tier Benefits Preview */}
                <SettingCard
                  title={t("rewards.customerTiers")}
                  icon={<Crown className="h-5 w-5" />}
                >
                  <TierPreview />
                </SettingCard>

                {/* Advanced Settings (Collapsed) */}
                <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
                  <Card>
                    <CardHeader>
                      <CollapsibleTrigger asChild>
                        <Button variant="ghost" className="w-full justify-between p-0">
                          <div className="flex items-center gap-2">
                            <div>
                              <CardTitle className="text-left">
                                {t("rewards.advancedSettings")}
                              </CardTitle>
                              <CardDescription className="text-left">
                                {t("rewards.advancedSettingsDescription")}
                              </CardDescription>
                            </div>
                          </div>
                          <ChevronDown
                            className={`h-4 w-4 transition-transform ${advancedOpen ? "rotate-180" : ""}`}
                          />
                        </Button>
                      </CollapsibleTrigger>
                    </CardHeader>
                    <CollapsibleContent>
                      <CardContent className="pt-0">
                        <AdvancedSettings />
                      </CardContent>
                    </CollapsibleContent>
                  </Card>
                </Collapsible>
              </div>
            ) : null}
          </div>
        </Form>
      </PageContent>
    </NavigationGuardProvider>
  );
}
